import { Button } from "../../components/Button";
import { ParentSurface } from "./ParentSurface";

interface ParentConsentScreenProps {
  /** The child's identifier — phone or email, since we don't collect a name during onboarding. */
  childIdentifier: string;
  state?: "pending" | "confirmed" | "expired" | undefined;
  onApprove?: (() => void) | undefined;
  onDecline?: (() => void) | undefined;
  onRequestNewLink?: (() => void) | undefined;
}

const COLLECTED = [
  "Phone number — to sign in and reach you",
  "Practice answers — to track what your child knows",
  "Exam and subject choices",
];

/**
 * Screen 22a and its two variants.
 *
 * The list is exhaustive on purpose: NDPA consent has to be specific, and a
 * parent deciding in thirty seconds on a phone needs the whole answer in one
 * screen rather than a link to a policy they will not open.
 *
 * The expired variant is deliberately calm — an expired link is the system
 * working (7 days), not the parent failing.
 *
 * We use the child's phone or email as the identifier because onboarding
 * collects neither a name nor an age — only phone, password, and optional
 * email. Fabricating an age or name the system does not know would be a lie
 * on a consent screen, which is the one place a lie has legal consequences.
 */
export function ParentConsentScreen({
  childIdentifier,
  state = "pending",
  onApprove,
  onDecline,
  onRequestNewLink,
}: ParentConsentScreenProps) {
  if (state === "confirmed") {
    return (
      <ParentSurface>
        <h1 className="m-0 pt-8 font-[family-name:var(--font-display)] text-[24px] font-bold leading-[1.2]">
          Done — your child can start.
        </h1>
        <p className="m-0 pt-3 text-[14px] leading-[1.6] text-text-muted">
          We've let them know. They can practise straight away, and you'll get a short progress note
          to this number now and then.
        </p>
        <p className="m-0 mt-auto pt-8 text-[12.5px] text-text-muted">
          You can withdraw this anytime by replying STOP to our SMS.
        </p>
      </ParentSurface>
    );
  }

  if (state === "expired") {
    return (
      <ParentSurface>
        <h1 className="m-0 pt-8 font-[family-name:var(--font-display)] text-[24px] font-bold leading-[1.2]">
          This link has expired.
        </h1>
        <p className="m-0 pt-3 text-[14px] leading-[1.6] text-text-muted">
          Permission links stop working after 7 days to keep your child's account safe. Nothing has
          gone wrong — you just need a fresh one.
        </p>

        <div className="mt-6 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
            Two ways to get a new link
          </p>
          <p className="m-0 pt-2.5 text-[13.5px] leading-[1.55]">
            Ask your child to tap "Resend to my parent" in the app — it arrives by SMS in seconds.
          </p>
          <p className="m-0 pt-2 text-[13.5px] leading-[1.55]">
            Or tap below and we'll send them a reminder for you.
          </p>
        </div>

        <div className="mt-auto pt-8">
          <Button onClick={onRequestNewLink}>Ask for a new link</Button>
          <p className="m-0 pt-4 text-center text-[12.5px] text-text-muted">
            Questions? WhatsApp us on 0700-PREPAI.
          </p>
        </div>
      </ParentSurface>
    );
  }

  return (
    <ParentSurface>
      <h1 className="m-0 pt-8 font-[family-name:var(--font-display)] text-[22px] font-bold leading-[1.25]">
        Your child wants to prepare for JAMB/WAEC with PrepAI.
      </h1>
      <p className="m-0 pt-3 text-[14px] leading-[1.6] text-text-muted">
        The account registered with{" "}
        <span className="font-semibold text-text">{childIdentifier}</span> is under 18, so Nigerian
        data law asks us to get your okay first. Here's everything we'd collect:
      </p>

      <ul className="m-0 flex list-none flex-col gap-2.5 p-0 pt-5">
        {COLLECTED.map((item) => (
          <li key={item} className="flex items-start gap-2.5 text-[13.5px] leading-[1.5]">
            <span aria-hidden="true" className="pt-0.5 text-accent">
              ·
            </span>
            {item}
          </li>
        ))}
      </ul>

      <p className="m-0 pt-4 text-[13px] font-semibold leading-[1.5]">
        No real name, no location. Nothing is sold or shared.
      </p>

      <div className="mt-auto flex flex-col gap-3 pt-8">
        <Button onClick={onApprove}>I approve — let them start</Button>
        <Button variant="ghost" onClick={onDecline}>
          Not now
        </Button>
        <p className="m-0 text-center text-[12.5px] text-text-muted">
          You can withdraw this anytime by replying STOP to our SMS.
        </p>
      </div>
    </ParentSurface>
  );
}
